import { useId, useState } from 'react';

import { formatDay } from '@/utils/format';

type Props = {
  /** One value per day, oldest first; the last point is yesterday. */
  values: number[];
  format: (v: number) => string;
  /** What the values are, for the accessible summary ("Daily GGR"). */
  label: string;
  width?: number;
  height?: number;
};

const PAD = 5; // room for the end dot and its ring

/**
 * A single-series trend line (dataviz sparkline): a 2px line in the de-emphasis
 * gray, the latest day marked in the accent with a 2px surface ring. Hover or
 * arrow keys move a crosshair that reads out the day and its value; the same
 * numbers are summarized in the accessible label, so the tooltip never gates.
 */
const Sparkline = ({ values, format, label, width = 112, height = 32 }: Props) => {
  const [active, setActive] = useState<number | null>(null);
  const tipId = useId();
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i: number) => PAD + (i * (width - 2 * PAD)) / (values.length - 1);
  const y = (v: number) => PAD + (1 - (v - min) / span) * (height - 2 * PAD);
  const path = values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  const dayOf = (i: number) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - (values.length - i));
    return d;
  };
  const last = values.length - 1;
  const summary = `${label}, last ${values.length} days: from ${format(values[0])} to ${format(values[last])}; low ${format(min)}, high ${format(max)}.`;

  const pick = (clientX: number, rect: DOMRect) => {
    const ratio = (clientX - rect.left) / rect.width;
    const i = Math.round(((ratio * width - PAD) / (width - 2 * PAD)) * last);
    setActive(Math.max(0, Math.min(last, i)));
  };

  return (
    <span className="relative inline-block">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={summary}
        aria-describedby={active !== null ? tipId : undefined}
        tabIndex={0}
        className="block cursor-crosshair overflow-visible rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/60"
        onPointerMove={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
        // A tap picks the point and keeps it shown until the chart loses focus: on a
        // touch screen the pointer "leaves" as soon as the finger lifts (UI-1).
        onPointerDown={(e) => pick(e.clientX, e.currentTarget.getBoundingClientRect())}
        onPointerLeave={(e) => e.pointerType !== 'touch' && setActive(null)}
        onBlur={() => setActive(null)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setActive((a) => Math.max(0, (a ?? last) - 1));
          else if (e.key === 'ArrowRight') setActive((a) => Math.min(last, (a ?? last) + 1));
          else if (e.key === 'Escape') setActive(null);
          else return;
          e.preventDefault();
        }}
      >
        {active !== null && <line x1={x(active)} x2={x(active)} y1={0} y2={height} className="stroke-line" strokeWidth={1} />}
        <path d={path} fill="none" className="stroke-viz-muted" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={x(last)} cy={y(values[last])} r={4} className="fill-viz-accent stroke-surface" strokeWidth={2} />
        {active !== null && active !== last && (
          <circle cx={x(active)} cy={y(values[active])} r={4} className="fill-viz-muted stroke-surface" strokeWidth={2} />
        )}
      </svg>
      {active !== null && (
        <span
          id={tipId}
          role="tooltip"
          className="pointer-events-none absolute bottom-full z-10 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-surface-raised px-2 py-1 text-xs shadow-e4"
          style={{ left: x(active) }}
        >
          <span className="font-semibold text-fg-strong">{format(values[active])}</span>
          <span className="ml-1.5 text-fg-muted">{formatDay(dayOf(active))}</span>
        </span>
      )}
    </span>
  );
};

export default Sparkline;
